import _ from "lodash";
import React, { useEffect, useState } from "react";
import styled, { StyledComponent } from "styled-components";
import { useTranslation } from "react-i18next";

import { ActionPermissionsObject, IWorkspacePlayerSettings, PermissionActionType, WorkspacePlanTier } from "common/interfaces";
import getObjectHash from "common/utils/getObjectHash";
import Banner from "js/Components/Banner";
import { Breadcrumb } from "js/Components/Breadcrumb";
import { getStaticUrl } from "js/config";
import WorkspaceController, { WorkspaceControllerState } from "js/controllers/WorkspaceController";
import getLogger, { LogGroup } from "js/core/logger";
import { PresentationPrivacyType } from "js/core/models/presentation";
import { FlexBox } from "js/react/components/LayoutGrid";
import { ShowErrorDialog, ShowDialog } from "js/react/components/Dialogs/BaseDialog";
import ChangePlanDialog from "js/react/components/Dialogs/ChangePlanDialog";
import { UIPane } from "js/react/components/UiComponents";
import { StackableSearchBarContainer, StackableUIPaneResultsContainer } from "js/react/views/AddSlide/Panes/Components/SearchBox";
import PlayerSettings from "js/react/views/PresentationSettings/components/PlayerSettings";

const logger = getLogger(LogGroup.WORKSPACES);

const Container = styled(UIPane)`
    width: 100%;
    overflow-y: scroll;
    display: block;
` as unknown as StyledComponent<"div", any, {}, never>;

const PlayerSettingsContainer = styled.div`
    background: white;
    padding: 20px;
`;

export default WorkspaceController.withInitializedState(
    function PlayerSettingsPane(props: WorkspaceControllerState) {
        const { workspace } = props;
        const { t } = useTranslation();

        const [playerSettings, setPlayerSettings] = useState<IWorkspacePlayerSettings>(_.cloneDeep(workspace.player.settings));
        const [fetching, setFetching] = useState(false);

        const canManageSharedResources = WorkspaceController.actionPermissions[ActionPermissionsObject.SHARED_RESOURCES].manage;
        const canUpgradeToManageSharedResources = WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.SHARED_RESOURCES, PermissionActionType.MANAGE);
        const canManagePlayerSettings = WorkspaceController.actionPermissions[ActionPermissionsObject.PLAYER_SETTINGS].manage;
        const canUpgradeToManagePlayerSettings = (canUpgradeToManageSharedResources && canManagePlayerSettings) || (canManageSharedResources && WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.PLAYER_SETTINGS, PermissionActionType.MANAGE));

        useEffect(() => {
            if (getObjectHash(workspace.player.settings) !== getObjectHash(playerSettings)) {
                setPlayerSettings(_.cloneDeep(workspace.player.settings));
            }
        }, [getObjectHash(workspace.player.settings)]);

        const handleChange = async (delta: Partial<IWorkspacePlayerSettings>) => {
            setFetching(true);

            try {
                // Just in case, you never know what these old components are sending
                const cleanDelta = _.pick(delta, ["showTitle", "showAuthor", "requireEmail", "showGetDeckUI", "allowPdfDownload"]);
                if (Object.keys(cleanDelta).length === 0) {
                    return;
                }

                // Update the UI immediately for smoother UX
                setPlayerSettings({
                    ...playerSettings,
                    ...cleanDelta,
                });

                await WorkspaceController.updateWorkspace({ player: { settings: { ...cleanDelta } } });
            } catch (error) {
                logger.error(error, "[PlayerSettingsPane] Error updating player settings");

                ShowErrorDialog({
                    title: t("Error updating player settings"),
                    message: t("An error occurred while updating the player settings. Please try again later."),
                });

                // Revert the UI to the previous state
                setPlayerSettings(playerSettings);
            } finally {
                setFetching(false);
            }
        };

        return (
            <Container>
                {!canUpgradeToManagePlayerSettings &&
                    <StackableSearchBarContainer className="search-container">
                        <Breadcrumb>{t("Player Settings")}</Breadcrumb>
                    </StackableSearchBarContainer>
                }
                {canUpgradeToManagePlayerSettings &&
                    <FlexBox center fillWidth padding="20px 0px 20px 20px">
                        <Banner
                            headerText={t("Unlock standardized Player Settings")}
                            bodyText={t("Upgrade to customize default player settings for your team.")}
                            buttonLabel={t("Upgrade")}
                            onButtonClick={() => ShowDialog(ChangePlanDialog,
                                {
                                    featuredPlan: WorkspacePlanTier.TEAM,
                                    analytics: { cta: "team_player_settings" }
                                }
                            )}
                        />
                    </FlexBox>
                }
                <StackableUIPaneResultsContainer>
                    <PlayerSettingsContainer className="player_settings">
                        <PlayerSettings
                            sectionTitle={t("Default Player Settings")}
                            removeBeautifulBranding
                            privacySetting={PresentationPrivacyType.PUBLIC}
                            slideThumbnail={getStaticUrl("/images/thumbnails/sample-slide-large.png")}
                            presentationName={t("Presentation Title")}
                            handleChange={handleChange}
                            showTitle={playerSettings.showTitle}
                            showAuthor={playerSettings.showAuthor}
                            requireEmail={playerSettings.requireEmail}
                            showGetDeckUI={playerSettings.showGetDeckUI}
                            allowPdfDownload={playerSettings.allowPdfDownload}
                            disabled={fetching || canUpgradeToManagePlayerSettings}
                        />
                    </PlayerSettingsContainer>
                </StackableUIPaneResultsContainer>
            </Container>
        );
    }
);
